import { Graphics } from 'pixi.js';
import { ComponentStore } from '@ecs/world';
import type {
	AmmoComponent,
	EnemyComponent,
	PlatformComponent,
	PlatformResource,
	ProjectileComponent,
	StageBounds,
	TransformComponent
} from '@game/scenes/characterPrototype/types';
import type { Tuning } from '@game/tuning';
import { clamp, randomInRange } from '@game/scenes/characterPrototype/math';

export function updatePlatformGeometry(bounds: StageBounds, platform: PlatformResource, tuning: Tuning) {
	const desiredWidth = platform.width || bounds.width * tuning.platformWidthRatio;
	const clampedWidth = Math.max(
		tuning.platformMinSideOffset * 2,
		Math.min(desiredWidth, bounds.width - tuning.platformMinSideOffset * 2)
	);
	platform.width = clampedWidth;
	platform.height = tuning.platformHeight;

	const maxX = Math.max(0, bounds.width - platform.width);
	const maxY = Math.max(0, bounds.height - platform.height);
	const defaultX = (bounds.width - platform.width) / 2;
	const defaultY = bounds.height - platform.height - tuning.platformDefaultBottom;

	platform.x = Number.isNaN(platform.x) ? defaultX : clamp(platform.x, 0, maxX);
	platform.y = Number.isNaN(platform.y) ? defaultY : clamp(platform.y, 0, maxY);
}

export function applyPlatformTuningToExisting(platforms: ComponentStore<PlatformComponent>, bounds: StageBounds, tuning: Tuning) {
	platforms.forEach((platform) => {
		platform.height = tuning.platformHeight;
		platform.width = clamp(platform.width, tuning.platformMinWidth, tuning.platformMaxWidth);
		const maxX = Math.max(0, bounds.width - platform.width);
		platform.x = clamp(platform.x, 0, maxX);
	});
}

export function applyEnemyTuningToExisting(
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	tuning: Tuning
) {
	enemies.forEach((enemy, entity) => {
		const t = transforms.get(entity);
		const g = graphics.get(entity);
		if (!t || !g) return;
		enemy.cooldown = randomInRange(tuning.enemyCooldownMin, tuning.enemyCooldownMax);
		if (!Number.isFinite(enemy.vx)) enemy.vx = 0;
		if (!Number.isFinite(enemy.vy)) enemy.vy = 0;
		if (!Number.isFinite(enemy.baseY)) enemy.baseY = t.y;
		if (!Number.isFinite(enemy.repath)) enemy.repath = 0;
		if (!Number.isFinite(enemy.desiredX)) enemy.desiredX = Number.NaN;
		if (!Number.isFinite(enemy.wanderPhase)) enemy.wanderPhase = Math.random() * Math.PI * 2;
		if (typeof enemy.hadLos !== 'boolean') enemy.hadLos = true;
		if (!Number.isFinite(enemy.burstShotsLeft)) enemy.burstShotsLeft = 0;
		if (!Number.isFinite(enemy.burstTimer)) enemy.burstTimer = 0;
		g.clear();
		g.rect(-tuning.enemySize / 2, -tuning.enemySize / 2, tuning.enemySize, tuning.enemySize);
		g.fill({ color: 0xff3b30, alpha: 0.95 });
	});
}

export function applyProjectileTuningToExisting(
	projectiles: ComponentStore<ProjectileComponent>,
	graphics: ComponentStore<Graphics>,
	tuning: Tuning
) {
	projectiles.forEach((projectile, entity) => {
		const g = graphics.get(entity);
		if (!g) return;
		const size = projectile.fromEnemy ? tuning.projectileSize : tuning.playerProjectileSize;
		projectile.size = size;
		g.clear();
		g.circle(0, 0, size / 2);
		if (projectile.fromEnemy) {
			g.fill({ color: 0x991b1b, alpha: 0.95 });
			g.stroke({ color: 0xfee2e2, alpha: 0.8, width: 1.5 });
		} else {
			g.fill({ color: 0x38bdf8, alpha: 0.95 });
			g.stroke({ color: 0xcffafe, alpha: 0.8, width: 1.5 });
		}
	});
}

export function applyAmmoTuningToExisting(
	ammos: ComponentStore<AmmoComponent>,
	playerId: number | null,
	tuning: Tuning,
	ammoClipSize: number
) {
	ammos.forEach((ammo, entity) => {
		const isEnemy = ammo.isEnemy;
		const reloadDuration = Math.max(0.05, isEnemy ? tuning.enemyReloadDuration : tuning.playerReloadDuration);
		ammo.capacity = ammoClipSize;
		ammo.reloadDuration = reloadDuration;
		ammo.remaining = clamp(Math.round(ammo.remaining), 0, ammo.capacity);
		if (!Number.isFinite(ammo.reloadRemaining) || ammo.reloadRemaining < 0) ammo.reloadRemaining = 0;
		if (!isEnemy && playerId != null && entity !== playerId) {
			// ignore: player ammo should only live on player entity
		}
	});
}


